/**
 * BAY WINDOW — proportions shared by `BayWindowGeometry.js` (3D) and the
 * bay branch of the window elevation schematic (`BayWindowSchematic.jsx`).
 * Re-exported from `proceduralSpecs.js`; split out so that file stays under
 * its size ceiling.
 *
 * PURE (no Three.js, no React). Units are METERS.
 *
 * The structure data for a bay window is an ordinary window opening —
 * `dimensions` = the daylight opening in the wall (width x height) — plus
 * `category.window.bayAngle` (30 | 45 | 90, see `WindowPanel.js`). There is
 * NO persisted projection depth: the bay's projection is DERIVED from the
 * opening width and the angle, which is why every consumer must go through
 * `bayWindowProfile()` rather than reading a depth off the object.
 *
 * Frame of reference for the profile (matches `WindowWithTrim` / the 3D):
 *   - x: centred on the opening (0 = middle of the width)
 *   - y: centred on the opening (0 = middle of the height); the daylight
 *        opening spans ±height/2 and every frame member sits OUTSIDE it,
 *        exactly like `WindowWithTrim.createFrame` (outer = size + 2t).
 *   - z: 0 = the wall's INTERIOR face. The unit projects to NEGATIVE z
 *        (exterior); only the casing and the stool nosing sit at z > 0.
 * The schematic flips y (SVG y grows downward) and, in a side view, uses z
 * as its x.
 */

export const IN_TO_M = 0.0254;

// Andersen / Pella catalogue bays are sold as 25/50/25 (flanker / centre /
// flanker) by WALL footprint: the centre unit is half the rough opening and
// each angled flanker covers the remaining quarter of the wall, so the
// unit fills the opening it was ordered for. The flanker SASH is therefore
// wider than its footprint (footprint / cos(angle)).
export const BAY_WINDOW = {
  centerFrac: 0.5,
  // Mirrors of `WindowWithTrim.create`'s defaults (`frameDepth` is fixed at
  // 4" there; the others are the destructuring defaults). The 3D receives
  // them as arguments; the schematic reads them from here so both sides
  // land on the same numbers for a window that carries no overrides.
  frameDepth: 4 * IN_TO_M,
  frameThickness: 0.06,
  trimWidth: 0.1, // interior casing width (same 4" as every window's trim)
  trimDepth: 0.02, // casing stands this proud of the wall face
  // Casement sub-frame (sash) thickness as a fraction of frameThickness —
  // `WindowWithTrim.createCasementPanels`' `subFrameThickness`.
  sashFrac: 0.6,
  // Seat board + head board: the 2" (nominal 5/4) boards that close the
  // bottom and top of a factory bay unit.
  boardThickness: 0.05,
  // The stool (interior seat-board nosing) projects 1" past the casing face
  // — the standard window-stool detail.
  stoolNosing: 1 * IN_TO_M,
  // Stool horns run half a casing width past each casing — the same
  // `width + 3 * trimWidth` rule `WindowWithTrim.createSill` uses.
  stoolHornFrac: 0.5,
  // The seat/head boards taper with the flankers; their front (exterior)
  // edge overhangs the centre unit's stiles by this fraction of the horn.
  boardFrontLipFrac: 0.5,
  supportedAngles: [30, 45, 90],
};

/**
 * Every derived number both the 3D and the 2D need, for one bay window.
 *
 * `bayAngle` is the angle between the flanker and the wall (degrees). At
 * 90° (a box bay) the flankers run straight out from the opening edges, so
 * the centre unit spans the FULL opening and the flanker sash width IS the
 * projection (a quarter of the opening, as for the angled bays).
 *
 * Depths are POSITIVE distances measured outward from the wall's interior
 * face (multiply by -1 for world z). `unitDepth` is the exterior face of the
 * whole unit: the centre frame's outer face, or the flanker's inner-stile
 * corner where that swings out past it.
 */
export function bayWindowProfile({
  width,
  bayAngle = 45,
  frameDepth = BAY_WINDOW.frameDepth,
  frameThickness = BAY_WINDOW.frameThickness,
  trimWidth = BAY_WINDOW.trimWidth,
  trimDepth = BAY_WINDOW.trimDepth,
}) {
  const angleRad = (bayAngle * Math.PI) / 180;
  const sin = Math.sin(angleRad);
  const cos = Math.cos(angleRad);
  const isBoxBay = bayAngle === 90;

  const wallFootprint = isBoxBay
    ? 0
    : (width * (1 - BAY_WINDOW.centerFrac)) / 2;
  const centerWidth = width - 2 * wallFootprint;
  const sideWidth = isBoxBay
    ? (width * (1 - BAY_WINDOW.centerFrac)) / 2
    : wallFootprint / cos;
  const projection = sideWidth * sin;

  // The whole unit is pushed out half a frame depth so the flanker frames
  // clear the wall face; the panel planes sit at -(projection + unitOffset).
  const unitOffset = frameDepth / 2;
  const centerExteriorDepth = projection + unitOffset + frameDepth;
  // The flanker pivots at the centre unit's edge; its inner stile
  // ([0, t] x [-frameDepth, 0] before rotation) is the corner that reaches
  // furthest out, its outer stile the one that comes back to the wall.
  const flankExteriorDepth =
    projection + unitOffset + frameThickness * sin + frameDepth * cos;
  const flankNearDepth =
    projection + unitOffset - (sideWidth + frameThickness) * sin;
  const unitDepth = Math.max(centerExteriorDepth, flankExteriorDepth);

  const casingWidth = trimWidth;
  const casingDepth = trimDepth;
  const stoolHorn = trimWidth * BAY_WINDOW.stoolHornFrac;
  const stoolNosing = casingDepth + BAY_WINDOW.stoolNosing;
  const boardBackHalfWidth = width / 2 + casingWidth + stoolHorn;
  const boardFrontHalfWidth =
    centerWidth / 2 + frameThickness + stoolHorn * BAY_WINDOW.boardFrontLipFrac;

  return {
    width,
    angleRad,
    sin,
    cos,
    isBoxBay,
    centerWidth,
    wallFootprint,
    sideWidth,
    projection,
    unitOffset,
    centerExteriorDepth,
    flankExteriorDepth,
    flankNearDepth,
    unitDepth,
    frameDepth,
    frameThickness,
    sashThickness: frameThickness * BAY_WINDOW.sashFrac,
    casingWidth,
    casingDepth,
    stoolHorn,
    stoolNosing,
    boardThickness: BAY_WINDOW.boardThickness,
    boardBackHalfWidth,
    boardFrontHalfWidth,
    // Overall built envelope, so a card / test can state the declared box.
    overallWidth: 2 * boardBackHalfWidth,
    overallDepth: unitDepth + stoolNosing,
  };
}
